/**
 * Shared trend math for admin KPI cards (home, revenue, pulse, etc.).
 * Pure helpers — no React. Keeps formatting consistent across surfaces.
 */

export type AdminTrendDirection = "up" | "down" | "neutral";

/** % change current vs previous. null when undefined (e.g. previous was 0 and current > 0). */
export function calculateTrendPct(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) {
    if (current === 0) return 0;
    return null;
  }
  const trend = ((current - previous) / Math.abs(previous)) * 100;
  return Number.isFinite(trend) ? Number(trend.toFixed(2)) : null;
}

export function formatTrendPct(pct: number, fractionDigits = 1): string {
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(fractionDigits)}%`;
}

export function trendDirectionFromValue(
  value: number | string | null | undefined,
  explicit?: AdminTrendDirection,
): AdminTrendDirection {
  if (explicit) return explicit;
  if (value == null) return "neutral";
  if (typeof value === "number") {
    if (value > 0) return "up";
    if (value < 0) return "down";
    return "neutral";
  }
  const num = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(num)) return "neutral";
  if (num > 0) return "up";
  if (num < 0) return "down";
  return "neutral";
}

/**
 * When invert is true, "up" is bad (e.g. pending backlog grew).
 * Returns the visual direction after inversion.
 */
export function applyTrendInvert(
  dir: AdminTrendDirection,
  invert?: boolean,
): AdminTrendDirection {
  if (!invert || dir === "neutral") return dir;
  return dir === "up" ? "down" : "up";
}

export function trendToStatShape(
  pct: number | null,
  opts?: { label?: string; invert?: boolean; fractionDigits?: number },
): {
  value: string;
  direction: AdminTrendDirection;
  label?: string;
} | null {
  if (pct == null || !Number.isFinite(pct)) return null;
  const raw = trendDirectionFromValue(pct);
  const direction = applyTrendInvert(raw, opts?.invert);
  return {
    value: formatTrendPct(pct, opts?.fractionDigits ?? 1),
    direction,
    label: opts?.label,
  };
}
